import {
  createImportRemoteListener,
  report,
  REPORT_TYPES,
} from '@adsk/exo-skeleton-api';
import {
  setExoSkeletonRemotes,
  setIsInExoSkeleton,
  type RuntimeRemoteInfo,
  getRemoteRuntime,
} from '@adsk/exo-skeleton-api/internal';

import { getRemoteOverride } from './getRemoteOverrides';
import {
  EXO_SKELETON_SCOPE_NAME,
  IMPORT_REMOTE_RUNTIME_EVENT_NAME,
} from '../consts';
import { getEnvConfig } from '../envs/envConfig';
import type { Remote } from '../envs/types';

declare const __webpack_public_path__: string;

export function getExoSkeletonAppRemoteEntry(): Remote {
  const url = `${__webpack_public_path__}assets-no-cache/remoteEntry.js`;

  return {
    moniker: 'ACCWPEXO',
    url,
    scope: EXO_SKELETON_SCOPE_NAME,
  };
}

function createRuntimeRemotes(): Record<string, RuntimeRemoteInfo> {
  const { remotes } = getEnvConfig();
  const runtimeRemotes: Record<string, RuntimeRemoteInfo> = {};

  for (const { scope, url, moniker } of [
    ...remotes,
    getExoSkeletonAppRemoteEntry(),
  ]) {
    const overrideUrl = getRemoteOverride(scope);
    runtimeRemotes[scope] = {
      override: Boolean(overrideUrl),
      moniker,
      get() {
        return Promise.resolve({
          url: overrideUrl || url,
          scope,
        });
      },
    };
  }

  return runtimeRemotes;
}

export function initializeExoSkeletonObject() {
  setIsInExoSkeleton();
}

export function initializeExoSkeletonRuntimeValues() {
  setExoSkeletonRemotes(createRuntimeRemotes());

  createImportRemoteListener({
    onImportRemote({ data: { scope, metadata } }) {
      const { moniker, override } = getRemoteRuntime(scope) || {};

      report({
        type: REPORT_TYPES.info,
        value: IMPORT_REMOTE_RUNTIME_EVENT_NAME,
        scope: EXO_SKELETON_SCOPE_NAME,
        context: {
          moniker,
          override,
          scope,
          metadata,
        },
      });
    },
  });
}

export function ensureRemote(scope: string): boolean {
  const { remotes } = getEnvConfig();

  if (!remotes) {
    throw new Error('ExoSkeleton remotes are not available');
  }

  return Boolean(remotes.find((remote) => remote.scope === scope));
}

function getServiceWorkerUrl() {
  const uniqueBuildId = process.env.APPLICATION_UNIQUE_BUILD_ID;
  const serviceWorkerUrl = `${__webpack_public_path__ || '/'}service-worker.js${typeof uniqueBuildId === 'string' ? `?${uniqueBuildId}` : ''}`;
  return serviceWorkerUrl;
}

export function initializeServiceWorker({
  disabled = false,
}: { disabled?: boolean } = {}) {
  if ('serviceWorker' in navigator) {
    const serviceWorkerUrl = getServiceWorkerUrl();

    navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        if (disabled && registration) {
          return registration.unregister().then(() =>
            report({
              type: REPORT_TYPES.info,
              value: 'service-worker-unregistered',
              scope: EXO_SKELETON_SCOPE_NAME,
              context: {},
            }),
          );
        } else {
          return null;
        }
      })
      .catch((error) => {
        report({
          type: REPORT_TYPES.error,
          value: error,
          scope: EXO_SKELETON_SCOPE_NAME,
          context: {
            error,
            serviceWorkerUrl,
          },
        });
      });

    if (disabled) {
      return;
    }

    navigator.serviceWorker
      .register(serviceWorkerUrl, { scope: '/', updateViaCache: 'all' })
      .then(() => {
        report({
          type: REPORT_TYPES.info,
          value: 'service-worker-registered',
          scope: EXO_SKELETON_SCOPE_NAME,
          context: {
            serviceWorkerUrl,
          },
        });
      })
      .catch((registrationError) => {
        report({
          type: REPORT_TYPES.error,
          value: registrationError,
          scope: EXO_SKELETON_SCOPE_NAME,
          context: {
            error: registrationError,
            serviceWorkerUrl,
          },
        });
      });
  }
}
